import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * EmbedSharedDeck — chromeless iframe-friendly viewer at /embed/s/:slug
 * Query params:
 *   ?autoplay=<seconds>   → auto-advance every N seconds (min 3, max 60)
 *   ?loop=1               → restart from slide 1 after last
 *   ?bg=<hex>             → override outer background (defaults to black)
 *   ?t=<unlock-token>     → for password-protected shares
 *
 * Password-protected shares that arrive here without a token show a compact
 * "unavailable" state rather than a full-page password form — embedders
 * shouldn't ask viewers to enter passwords inside a foreign iframe.
 */
export default function EmbedSharedDeck() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const autoplay = Math.max(0, Math.min(60, parseInt(params.get("autoplay") || "0", 10) || 0));
  const loop = params.get("loop") === "1";
  const bg = params.get("bg") || null;
  const tokenParam = params.get("t") || "";

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const qs = tokenParam ? { t: tokenParam } : {};
        const res = await axios.get(`${API}/public/share/${slug}`, { params: qs });
        setData(res.data);
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 410) setError("This deck has expired.");
        else if (status === 401 && detail === "password_required") setError("This deck is password protected and can't be embedded without a token.");
        else setError("Share link isn't available.");
      }
    };
    if (slug) load();
  }, [slug, tokenParam]);

  const total = data?.project?.slides?.length || 0;

  useEffect(() => {
    if (!autoplay || autoplay < 3 || total <= 1) return undefined;
    timerRef.current && clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setIndex((i) => {
        if (i + 1 < total) return i + 1;
        return loop ? 0 : i;
      });
    }, autoplay * 1000);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [autoplay, index, total, loop]);

  if (error) {
    return (
      <div className="w-full h-screen bg-slate-950 text-slate-400 flex items-center justify-center text-sm">
        {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="w-full h-screen bg-slate-950 text-slate-500 flex items-center justify-center text-sm animate-pulse">
        Loading…
      </div>
    );
  }

  const current = data.project?.slides?.[index] || {};

  return (
    <div
      className="w-full h-screen flex flex-col cursor-pointer select-none"
      style={{ background: bg || "#000" }}
      onClick={() => setIndex((i) => (i + 1 < total ? i + 1 : loop ? 0 : i))}
      data-testid="embed-shared-deck"
    >
      <div className="flex-1 flex items-center justify-center px-6 py-4">
        {total === 0 ? (
          <div className="text-slate-500">Empty deck.</div>
        ) : (
          <div
            className="w-full max-w-6xl aspect-video rounded-md flex items-center justify-center px-12 py-6 text-center shadow-2xl"
            style={{
              background: current.background_color || "linear-gradient(135deg,#1e293b,#0f172a)",
              color: current.text_color || "#f8fafc",
            }}
            data-testid={`embed-slide-${index}`}
          >
            <div className="space-y-4">
              {current.title && (
                <h1 className="font-bold leading-tight"
                    style={{ fontSize: "clamp(24px, 5vw, 56px)" }}>
                  {current.title}
                </h1>
              )}
              {current.content && (
                <p className="whitespace-pre-wrap leading-relaxed"
                   style={{ fontSize: "clamp(18px, 3vw, 34px)" }}>
                  {current.content}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Compact bottom strip — dots + brand */}
      <div className="px-4 py-2 flex items-center justify-between border-t border-white/5">
        <div className="flex gap-1" data-testid="embed-dots">
          {Array.from({ length: total }).map((_, i) => (
            <span
              key={i}
              className={`w-1.5 h-1.5 rounded-full transition-all ${
                i === index ? "bg-amber-400 w-4" : "bg-white/25"
              }`}
            />
          ))}
        </div>
        <a
          href={`/s/${slug}`}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-[10px] uppercase tracking-widest text-white/40 hover:text-white/70"
        >
          Made with SlideCon
        </a>
      </div>
    </div>
  );
}
